import {Fragment} from "react";
import type {
    ReservationListAliasAgeCategoriesDTO,
} from "crqlar-react-common/dist/models/aggregation/reservationListAggregation.model";
import {
    type AgeCategory,
} from "crqlar-react-common/dist/models/ageCategory/ageCategory.model";
import {AgeCategoryElement, type AgeCategoryElementVariant} from "./AgeCategoryElement";
import {ElementChipList} from "./base/ElementChipList";
import Box from "@mui/material/Box";
import {hasConfiguredAgeCategoryAliases} from "../ageCategoryDefaults";

interface AliasAgeCategoriesElementProps {
    aliasAgeCategories?: ReservationListAliasAgeCategoriesDTO;
    ageCategories: AgeCategory[];
    asPrint?: boolean;
    variant?: AgeCategoryElementVariant;
}

export function AliasAgeCategoriesElement({
    aliasAgeCategories,
    ageCategories,
    asPrint = false,
    variant = "chip",
}: AliasAgeCategoriesElementProps) {
    if (!hasConfiguredAgeCategoryAliases(ageCategories)) return null;

    const entries = (aliasAgeCategories ?? [])
        .filter(({label, count}) => label.trim().length > 0 && count > 0)
        .map(entry => {
            const ageCategory = ageCategories.find(category => category.id === entry.id);
            const type = entry.type ?? ageCategory?.type;
            return {
                ...entry,
                ageCategory: {
                    id: entry.id,
                    name: ageCategory?.name,
                    type,
                    alias: entry.label.trim(),
                    color: ageCategory?.color,
                },
            };
        });

    if (!entries.length) return null;

    if (asPrint) {
        return <>{entries.map(({id, ageCategory, count}, index) => (
            <Fragment key={id}>
                {index > 0 && ", "}
                <AgeCategoryElement
                    ageCategory={ageCategory}
                    count={count}
                    asPrint
                />
            </Fragment>
        ))}</>;
    }

    if (variant === "inline") {
        return (
            <Box sx={{display: "flex", alignItems: "center", gap: 0.75, flex: "0 0 auto"}}>
                {entries.map(({id, ageCategory, count}) => (
                    <AgeCategoryElement
                        key={id}
                        ageCategory={ageCategory}
                        count={count}
                        variant="inline"
                    />
                ))}
            </Box>
        );
    }

    return (
        <ElementChipList>
            {entries.map(({id, ageCategory, count}) => (
                <AgeCategoryElement
                    key={id}
                    ageCategory={ageCategory}
                    count={count}
                />
            ))}
        </ElementChipList>
    );
}
